/**
 * 설정 드로어 — §14 host 콘솔의 `drawer`(설정).
 * @cupia/ui 의 Drawer(vaul 기반)를 그대로 쓴다.
 */

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@cupia/ui/drawer'
import { Button } from '@cupia/ui/button'
import { Field } from '@cupia/ui/field'
import { Badge } from '@cupia/ui/badge'
import { Separator } from '@cupia/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cupia/ui/select'
import type { FeedOrder } from '../lib/feed-order'
import { NON_UNIFIED_DEFAULT_SPLIT_MODE, type SplitMode } from '../lib/split-view'
import { MAX_STEP, MIN_STEP, clampStep } from '../lib/caption-scale'
import { CaptionSizeStepper } from './caption-size-stepper'
import type { CaptureStats } from '../lib/capture'
import type { LinkStats } from '../lib/use-room-stream'
import type { WakeLockStatus } from '../lib/wake-lock'
import { SwitchRow } from './switch-row'

function wakeLockStatusLabel(status: WakeLockStatus): string | null {
  switch (status) {
    case 'unsupported':
      return '이 브라우저는 화면 켜짐 유지를 지원하지 않습니다.'
    case 'denied':
      return '화면 켜짐 유지 요청이 거부되었습니다.'
    default:
      return null
  }
}

export interface HostSettings {
  /** 코치 피드 on/off — §16 TRANS_COACH */
  coach: boolean
  /**
   * 자막 기록 opt-in — §11. **방의 상태이지 화면 취향이 아니다**:
   * 서버(`GET /api/rooms/{code}/settings`)에서 읽어 온다.
   */
  record: boolean
  /** 보존 기간(§11 — 방별 7/30/90). 이것도 방의 상태다. */
  retentionDays: 7 | 30 | 90
  /** 구 서버 호환 — 이제 항상 false(§11.3 도중 토글 가능) */
  recordLocked: boolean
  /** 서버가 무저장으로 떠 있으면 켤 수 없다 — 이유를 말해야 한다 */
  recordAvailable: boolean
}

export interface SettingsDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  settings: HostSettings
  onChange: (settings: HostSettings) => void
  /** 자막 크기 단계(0–8) — 이 브라우저의 취향이다. */
  step: number
  onStepChange: (step: number) => void
  /** 자막 정렬 방향 — 이 브라우저의 취향이다(방 상태가 아니다). */
  order: FeedOrder
  onOrderChange: (order: FeedOrder) => void
  /** 분할 보기 — 정렬과 독립, 이 브라우저의 취향이다(D1). */
  splitMode: SplitMode
  onSplitModeChange: (mode: SplitMode) => void
  /**
   * 원문('들린 내용') 가림 — 이 브라우저의 취향이다(lib/source-veil.ts).
   * 헤더의 "원문 가리기" 버튼과 **같은 상태**를 본다: 진실은 useSourceVeil
   * 하나이고, 이 서랍의 스위치는 그 상태의 또 다른 손잡이일 뿐이다.
   */
  veiled: boolean
  onVeiledChange: (veiled: boolean) => void
  /**
   * 화면 켜짐 유지 — 이 브라우저의 취향이다(lib/wake-lock.ts 의
   * useWakeLockPref). 켜져 있으면 마이크 시작 등 방 참여 중 화면이
   * 꺼지지 않는다.
   */
  wakeLock: boolean
  onWakeLockChange: (wakeLock: boolean) => void
  /** 실제 잠금 상태(active|denied|unsupported|released) — 취향(wakeLock)과 별개다. */
  wakeLockStatus: WakeLockStatus
  stats: CaptureStats | null
  /**
   * D3 — 업링크 왕복 상태(`pong` 파생). `null` 이면 그 줄을 렌더하지
   * 않는다 — 없는 값을 0 으로 그리면 "서버가 0dB 를 받고 있다" 로 읽힌다.
   */
  linkStats?: LinkStats | null
  room: string
  /** 6자리 입장 패스코드 — 구두 공유용(§10.0). URL 에는 싣지 않는다 */
  passcode?: string | undefined
  /** 현재 통역 방향 문구("베트남어 → 한국어") — 서버 상태에서 파생 */
  direction?: string | undefined
  viewUrl: string
}

export function SettingsDrawer({
  open,
  onOpenChange,
  settings,
  onChange,
  step,
  onStepChange,
  order,
  onOrderChange,
  splitMode,
  onSplitModeChange,
  veiled,
  onVeiledChange,
  wakeLock,
  onWakeLockChange,
  wakeLockStatus,
  stats,
  linkStats,
  room,
  passcode,
  direction,
  viewUrl,
}: SettingsDrawerProps): React.ReactElement {
  const patch = (next: Partial<HostSettings>): void => onChange({ ...settings, ...next })

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <div className="mx-auto w-full max-w-lg overflow-y-auto">
          <DrawerHeader>
            <DrawerTitle>방 설정</DrawerTitle>
            <DrawerDescription>
              표시·기록·발표 모드를 조정합니다. 오디오는 어떤 설정에서도 저장되지 않습니다.
            </DrawerDescription>
          </DrawerHeader>

          <div className="flex flex-col gap-4 px-4 pb-4">
            <section className="flex flex-col gap-3">
              <h3 className="text-(length:--text-sm-size) font-semibold">표시</h3>

              {/* 가림은 **시각만**이다(lib/source-veil.ts) — 문구가 그 사실을
                  말하지 않으면 "껐으니 기록에서도 빠지겠지" 라고 오해한다. */}
              <SwitchRow
                label="원문(들린 내용) 표시"
                description="꺼도 화면에서만 가려집니다 — 복사·회의록에는 원문이 그대로 남습니다."
                checked={!veiled}
                onCheckedChange={(v) => onVeiledChange(!v)}
              />

              <SwitchRow
                label="코치 피드"
                checked={settings.coach}
                onCheckedChange={(v) => patch({ coach: v })}
              />

              <SwitchRow
                label="화면 켜짐 유지"
                description={
                  wakeLockStatusLabel(wakeLockStatus) ?? '회의 중 화면이 꺼지지 않게 유지합니다.'
                }
                checked={wakeLock}
                onCheckedChange={onWakeLockChange}
              />

              {/* 정렬은 **표시 방식**일 뿐이다 — 서버로 가지 않고, 자막을
                  다시 불러오지도 않는다(lib/feed-order.ts 주석 참조). */}
              <Field
                label="자막 정렬"
                orientation="stacked"
                help="최신 자막이 나타나는 위치입니다. 이 브라우저에만 적용됩니다."
              >
                <Select
                  value={order}
                  onValueChange={(v) => onOrderChange(v as FeedOrder)}
                >
                  <SelectTrigger data-testid="order-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest-first">최신이 위</SelectItem>
                    <SelectItem value="newest-last">최신이 아래</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              {/* 분할 보기(D1) — 정렬과 독립이다. 발표 모드에서도 유효하다.
                  켜진 상태는 `'split'`(v4 2열)과 `'rows'`(행 정렬) 둘 다
                  이므로 판정은 "통합이 아닌가" 로 본다. 켜는 쪽은 고정값
                  `NON_UNIFIED_DEFAULT_SPLIT_MODE`(='split')로 간다 —
                  기본값 자체가 'unified'(2026-09-22)가 되면서
                  `defaultSplitMode` 로는 더 이상 "켜기" 목적지를 표현할 수
                  없다. `'rows'` 를 쓰던 사용자는 저장값이 유지되므로 이
                  스위치와 무관하다. */}
              <SwitchRow
                label="분할 보기"
                description="왼쪽 실시간 · 오른쪽 긴호흡 기록을 나란히 봅니다."
                checked={splitMode !== 'unified'}
                onCheckedChange={(v) =>
                  onSplitModeChange(v ? NON_UNIFIED_DEFAULT_SPLIT_MODE : 'unified')
                }
              />

              <Field
                label="자막 크기"
                orientation="stacked"
                help="Alt + 또는 Alt − 로도 조절할 수 있습니다. 이 브라우저에만 적용됩니다."
              >
                <CaptionSizeStepper
                  step={step}
                  onIncrease={() => onStepChange(clampStep(step + 1))}
                  onDecrease={() => onStepChange(clampStep(step - 1))}
                  canIncrease={step < MAX_STEP}
                  canDecrease={step > MIN_STEP}
                />
              </Field>
            </section>

            <Separator />

            <section className="flex flex-col gap-3">
              <h3 className="text-(length:--text-sm-size) font-semibold">기록</h3>
              {/* 이 두 값은 **방의 상태**다 — 서버에서 읽어 그대로 보여 준다.
                  클라이언트 기본값을 그리면 기록 ON 인 방이 OFF 로 보인다
                  (실제로 그랬다). §11.3 이후 회의 중 토글이 가능하고, 켜기는
                  **지금 화면의 자막까지 소급 저장**한다 — 문구가 그 사실을
                  말하지 않으면 호스트가 범위를 잘못 안다. */}
              <SwitchRow
                label="자막 기록"
                description={
                  !settings.recordAvailable
                    ? '이 서버는 기록을 저장하지 않도록 구성되어 있습니다.'
                    : settings.record
                      ? '회의 중 언제든 끌 수 있습니다. 오디오는 항상 미저장입니다.'
                      : '켜면 지금 화면에 있는 자막까지 함께 저장됩니다. 오디오는 항상 미저장입니다.'
                }
                checked={settings.record}
                disabled={!settings.recordAvailable}
                onCheckedChange={(v) => patch({ record: v })}
              />

              {/*
                끄기의 의미를 분명히 한다 — 토글을 삭제로 오해하면 "껐으니
                지워졌겠지" 라고 믿게 된다. 실제로는 보존 기간까지 남는다.
              */}
              {settings.record && (
                <p
                  data-testid="record-keep-note"
                  className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]"
                >
                  기록을 꺼도 이미 저장된 자막은 보존 기간까지 유지됩니다.
                </p>
              )}

              {settings.record && (
                <Field label="보존 기간" orientation="stacked">
                  <Select
                    value={String(settings.retentionDays)}
                    disabled={settings.recordLocked}
                    onValueChange={(v) => patch({ retentionDays: Number(v) as 7 | 30 | 90 })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7일</SelectItem>
                      <SelectItem value="30">30일</SelectItem>
                      <SelectItem value="90">90일</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </section>

            <Separator />

            <section className="flex flex-col gap-2">
              <h3 className="text-(length:--text-sm-size) font-semibold">입장 정보</h3>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-(length:--text-xs-size)">
                <dt className="text-[var(--color-muted-foreground)]">방</dt>
                <dd className="font-mono">{room}</dd>
                {direction && (
                  <>
                    <dt className="text-[var(--color-muted-foreground)]">통역 방향</dt>
                    <dd>{direction}</dd>
                  </>
                )}
                {passcode && (
                  <>
                    <dt className="text-[var(--color-muted-foreground)]">입장 코드</dt>
                    <dd className="font-mono tracking-widest">{passcode}</dd>
                  </>
                )}
                <dt className="text-[var(--color-muted-foreground)]">뷰어 링크</dt>
                <dd className="truncate font-mono">{viewUrl}</dd>
              </dl>
            </section>

            {stats && (
              <>
                <Separator />
                <section className="flex flex-col gap-2">
                  <h3 className="text-(length:--text-sm-size) font-semibold">오디오 전송</h3>
                  <div className="flex flex-wrap gap-1.5">
                    <Badge variant="softGray">{stats.sampleRate || '—'} Hz</Badge>
                    <Badge variant={stats.resampled ? 'softWarn' : 'softSuccess'}>
                      {stats.resampled ? 'FIR 폴백 리샘플링' : '네이티브 16k'}
                    </Badge>
                    <Badge variant="softGray">전송 {stats.sent}</Badge>
                    <Badge variant={stats.dropped > 0 ? 'softDanger' : 'softGray'}>
                      드롭 {stats.dropped}
                    </Badge>
                    {/* D3 — 사유별 내역. 합계(위 배지)는 유지하고 여기서
                        어느 경로로 잃었는지를 가른다: 적체는 대역이 못
                        따라간 것이고, 보류 초과는 단절이 길어진 것이다. */}
                    <Badge
                      variant={stats.droppedBacklog > 0 ? 'softWarn' : 'softGray'}
                    >
                      드롭(적체) {stats.droppedBacklog}
                    </Badge>
                    <Badge
                      variant={stats.droppedStallEvict > 0 ? 'softWarn' : 'softGray'}
                    >
                      드롭(보류초과) {stats.droppedStallEvict}
                    </Badge>
                    <Badge variant="softGray">
                      버퍼 {(stats.buffered / 1024).toFixed(0)}KB
                    </Badge>
                  </div>
                  {/* D3 — 서버가 **실제로 받은 것**. 클라 미터와 갈리는
                      순간이 곧 업링크 고장의 서명이다. pong 미수신이면
                      렌더하지 않는다. */}
                  {linkStats && (
                    <p
                      className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]"
                      data-testid="link-stats"
                    >
                      {linkStats.rttMs === null ? 'RTT —' : `RTT ${linkStats.rttMs}ms`}
                      {' · '}
                      {linkStats.ack === null ? 'ack —' : `ack ${linkStats.ack}`}
                      {' · '}
                      {linkStats.serverDbfs === null
                        ? '서버 —'
                        : `서버 ${linkStats.serverDbfs.toFixed(0)}dB`}
                    </p>
                  )}
                </section>
              </>
            )}
          </div>

          <DrawerFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              닫기
            </Button>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
